// Environment Configuration
// This helps manage different environments (development, Docker, mobile testing, production)

export interface EnvironmentConfig {
  apiBaseUrl: string;
  isDevelopment: boolean;
  isDocker: boolean;
  isMobile: boolean;
  isNgrok: boolean;
  isTunnel: boolean;
  authTransport: 'cookie' | 'bearer';
}

// Known dev-tunnel hosts (ngrok, Cloudflare quick tunnels). When the app is
// served through one of these, the live origin IS the public origin, so API and
// absolute media URLs are derived from window.location.origin at runtime rather
// than baked in via VITE_API_URL. Shared with src/index.tsx (service-worker
// gating) so both agree on what counts as a tunnel. index.html's load watchdog
// runs before any module loads, so it keeps its own copy of this list — update
// both together.
export const TUNNEL_HOSTS = [
  'ngrok.dev',
  'ngrok-free.dev',
  'ngrok.io',
  'ngrok-free.app',
  'ngrok.app',
  'trycloudflare.com',
];

export const isTunnelHost = (hostname: string): boolean =>
  TUNNEL_HOSTS.some(host => hostname === host || hostname.endsWith(`.${host}`));

const getEnvironmentConfig = (): EnvironmentConfig => {
  const hostname = window.location.hostname;
  const viteApiBaseUrl = normalizePublicApiOrigin(
    typeof import.meta !== 'undefined'
      ? import.meta.env?.VITE_API_URL
      : undefined
  );

  // Check if we're in development (localhost)
  const isDevelopment = hostname === 'localhost' || hostname === '127.0.0.1';

  // Check if we're in Docker (container hostname or Docker network IP)
  // Docker containers typically have hostnames like:
  // - Random container names (e.g., "snowball-frontend-1")
  // - Docker network IPs (e.g., "172.18.0.4")
  // - Container hostnames that don't contain "docker" or "container"
  const isDocker =
    hostname.includes('docker') ||
    hostname.includes('container') ||
    hostname.startsWith('172.') || // Docker default network range
    hostname.startsWith('10.') || // Docker custom network range
    hostname.startsWith('192.168.') || // Docker bridge network
    !!hostname.match(/^[a-f0-9]{12}$/) || // Docker container ID (12 hex chars)
    (hostname.includes('-') && !hostname.includes('.')); // Container names with dashes

  // Check if we're served through a dev tunnel (ngrok or Cloudflare quick tunnel)
  const isTunnel = isTunnelHost(hostname);
  // Retained for diagnostics: specifically the ngrok subset of tunnel hosts.
  const isNgrok = hostname.includes('ngrok');

  // Check if we're on mobile (mobile IP addresses)
  const isMobile = !isDevelopment && !isDocker && !isTunnel;

  let apiBaseUrl: string;

  if (viteApiBaseUrl) {
    // Explicit public backend override for API calls
    // and absolute preview media URLs, even when the frontend is local.
    apiBaseUrl = viteApiBaseUrl;
  } else if (isDevelopment) {
    // Development: use empty string (relative URLs) to leverage the proxy
    // The proxy in package.json will forward /api/* to http://localhost:8080
    apiBaseUrl = '';
  } else if (isTunnel) {
    // Dev tunnel (ngrok / Cloudflare): use the live origin so API and absolute
    // media URLs (e.g. SMS og:image) resolve through the tunnel. Derived at
    // runtime — no fixed VITE_API_URL is baked in, so the same build still works
    // when hit via localhost/LAN (e.g. Cypress) without firing cross-origin.
    apiBaseUrl = window.location.origin;
  } else if (isDocker) {
    // Docker: use localhost:8080 for development (Docker network URLs aren't accessible from host browser)
    apiBaseUrl = 'http://localhost:8080';
  } else {
    // Production (Cloud Run / custom domain): same origin, no port needed
    apiBaseUrl = '';
  }

  // Auth transport: declares the channel this client uses to carry the JWT.
  // 'cookie' (default) preserves HttpOnly + SameSite=Strict protection against
  // XSS. 'bearer' is an explicit opt-in for cross-origin deployments where the
  // cookie jar doesn't traverse (e.g. frontend on one host, backend on another).
  // localStorage override allows runtime switching (e.g. local e2e front-back-split).
  const runtimeTransport = localStorage.getItem('__snowball_auth_transport');
  const viteTransport =
    typeof import.meta !== 'undefined'
      ? import.meta.env?.VITE_AUTH_TRANSPORT
      : undefined;
  const rawTransport = runtimeTransport || viteTransport;
  const authTransport: 'cookie' | 'bearer' =
    rawTransport === 'bearer' ? 'bearer' : 'cookie';

  return {
    apiBaseUrl,
    isDevelopment,
    isDocker,
    isMobile,
    isNgrok,
    isTunnel,
    authTransport,
  };
};

export const ENV = getEnvironmentConfig();

function normalizePublicApiOrigin(value: string | undefined): string {
  const origin = normalizeOrigin(value);
  if (!origin) return '';

  try {
    const { hostname } = new URL(origin);
    if (hostname === 'app') return '';
  } catch {
    return '';
  }

  return origin;
}

function normalizeOrigin(origin: string | undefined): string {
  const value = origin?.trim();
  if (!value) return '';
  const withScheme = /^https?:\/\//i.test(value) ? value : `https://${value}`;
  return withScheme.replace(/\/+$/, '');
}

// Debug logging (always show, helpful for debugging environment detection)
// eslint-disable-next-line no-console
console.log('🌍 Environment Config:', {
  apiBaseUrl: ENV.apiBaseUrl,
  viteApiBaseUrl:
    typeof import.meta !== 'undefined'
      ? normalizePublicApiOrigin(import.meta.env?.VITE_API_URL)
      : '',
  isDevelopment: ENV.isDevelopment,
  isNgrok: ENV.isNgrok,
  isTunnel: ENV.isTunnel,
  isDocker: ENV.isDocker,
  isMobile: ENV.isMobile,
  authTransport: ENV.authTransport,
  hostname: window.location.hostname,
  origin: window.location.origin,
  port: window.location.port,
});
